import DayjsUtils from '@date-io/dayjs';
import { FormControl, FormLabel, FormHelperText, InputAdornment, IconButton } from '@material-ui/core';
import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import TodayIcon from '@material-ui/icons/Today';
import { MuiPickersUtilsProvider, DatePicker, DatePickerProps } from '@material-ui/pickers';
import { useField, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import useCommonStyles from '../../useCommonStyles';

export interface OutlinedDatePickerFieldProps extends Omit<DatePickerProps, 'value' | 'onChange'> {
    name: string;
}

const defaultMaterialTheme = createTheme({
    palette: {
        primary: {
            main: '#D5001C',
            contrastText: '#fff',
        },
        secondary: {
            main: '#D5001C',
            contrastText: '#fff',
        },
    },
    typography: {
        fontFamily: 'Porsche Next !important',
    },
});

const OutlinedDatePickerField = ({ name, label, required, ...props }: OutlinedDatePickerFieldProps) => {
    const { t } = useTranslation('common');
    const { disabled } = props;
    const [field, meta] = useField({ name });
    const { setFieldValue } = useFormikContext();
    const commonStyles = useCommonStyles();

    const hasError = !!meta.error && meta.touched;

    return (
        <FormControl fullWidth>
            {label && (
                <FormLabel className={commonStyles.formLabel} disabled={disabled} required={required}>
                    {label}
                </FormLabel>
            )}
            <ThemeProvider theme={defaultMaterialTheme}>
                <MuiPickersUtilsProvider utils={DayjsUtils}>
                    <DatePicker
                        // by default auto complete is off but props can always override it
                        autoComplete="off"
                        // spread props
                        {...props}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton>
                                        <TodayIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        // the error is defined from what we go in meta data
                        error={hasError}
                        // date format
                        format={t('common:formats.datePickerFormat')}
                        inputVariant="outlined"
                        onChange={value => setFieldValue(name, value.startOf('day').toDate())}
                        value={field.value}
                    />
                </MuiPickersUtilsProvider>
            </ThemeProvider>
            <FormHelperText error={hasError}>{hasError ? meta.error : ''}</FormHelperText>
        </FormControl>
    );
};

export default OutlinedDatePickerField;
